import { createRouter, createWebHistory, RouteLocationNormalized } from 'vue-router';
import Login from '@/views/Login.vue'
import Media from '@/views/Media.vue'
import MediaItem from '@/views/MediaItem.vue'
import Members from '@/views/Members.vue'
import Groups from '@/views/Groups.vue'
import Uploader from '@/views/Uploader.vue'
import CustomApplication from '@/views/CustomApplication.vue'
import Channels from '@/views/Channels.vue'
import Member from '@/views/Member.vue'
import Dashboard from '@/views/Dashboard.vue'
import Settings from '@/views/Settings.vue'
import Profile from '@/views/Profile.vue'
import Application from '@/views/Application.vue'
import Applications from '@/views/Applications.vue'
import Contests from '@/views/Contests.vue'
import Collections from '@/views/Collections.vue'
import Reports from '@/views/Reports.vue'
import TransferManager from '@/views/TransferManager.vue'
import FeedManager from '@/views/FeedManager.vue'
import NotificationManager from '@/views/NotificationManager.vue'
import WidgetManager from '@/views/WidgetManager.vue'
import ValidationManager from '@/views/ValidationManager.vue'
import AuditLog from '@/views/AuditLog.vue'
import BuilderView from '@/views/BuilderView.vue'
import PreviewView from '@/views/PreviewView.vue'
import Permissions from '@/views/Permissions.vue'
import MetadataConfig from '@/views/MetadataConfig.vue'
import ConverterQueue from '@/views/ConverterQueue.vue'
import Gamification from '@/views/Gamification.vue'
import MediaBrowser from '@/views/mediamanager/MediaBrowser.vue'
import MLTraining from '@/views/MLTraining.vue'
import GroupDetail from '@/views/GroupDetail.vue'
import ErrorPage from '@/views/ErrorPage.vue';
import VideoPortal from '@/views/VideoPortal.vue'
import ApiTest from '@/views/ApiTest.vue'
import Credentials from '@/views/Credentials.vue'

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		{
			path: '/login',
			name: 'login',
			component: Login
		},
		{
			path: '/',
			name: 'Home',
			component: Dashboard,
			meta: {
				breadcrumb: 'Dashboard',
			}
		},
		{
			path: '/home',
			name: 'Dashboard',
			component: Dashboard,
			meta: {
				breadcrumb: 'Dashboard',
				description: 'Gain insights at a glance with the Dashboard. View key statistics, including recent uploads, new users, and platform usage trends, to stay informed and make data-driven decisions.',
			},
		},
		{
			path: '/application/:appId?',
			name: 'Application',
			component: Application,
			props: true,
			meta: {
				breadcrumb: 'Templates',
				description: 'Manage page templates, wrappers, email templates, and CSS for your application.',
			},
		},
		{
			path: '/applications',
			name: 'Applications',
			component: Applications,
			meta: {
				breadcrumb: 'Your Applications',
				description: 'Gain insights at a glance with the Dashboard. View key statistics, including recent uploads, new users, and platform usage trends, to stay informed and make data-driven decisions.',
			},
		},
		{
			path: '/channels',
			name: 'Channels',
			component: Channels,
			meta: {
				breadcrumb: 'Channels List',
				description: " Browse and manage your categorized content this hierarchical channel list."
			},
		},
		{
			path: '/custom',
			name: 'Custom Application',
			component: CustomApplication,
			meta: {
				breadcrumb: 'Application Builder',
				description: "Build your site visually — no coding required."
			},
		},
		{
			path: '/groups',
			name: 'Groups',
			component: Groups,
			meta: {
				breadcrumb: 'Group List',
				description: " Browse and manage your categorized content and users with this hierarchical group list."
			},
		},
		{
			path: '/groups/:id',
			name: 'Group',
			component: GroupDetail,
			meta: {
				breadcrumb: (route: RouteLocationNormalized) => ` ${route.params.id}`,
				parent: 'Groups',
				description: '',
			},
		},
		{
			path: '/upload',
			name: 'Upload',
			component: Uploader,
			meta: {
				breadcrumb: 'Upload',
				description: "Easily upload your media or articles. Select files or compose rich HTML content with group and channel assignments. Get a signed upload URL, track progress, and finalize submissions with real-time status updates."
			},
		},
		{
			path: '/profile',
			name: 'Profile',
			component: Profile,
			meta: {
				breadcrumb: 'Profile',
				description: 'Personalize your experience on the User Profile page. Update your account settings, manage preferences, and ensure your profile information is always up to date.',
			},
		},
		{
			path: '/credentials',
			name: 'Credentials',
			component: Credentials,
			meta: {
				breadcrumb: 'Credentials',
				description: 'Manage AI provider keys, integration credentials, client transfers, and developer API keys.',
			},
		},
		{
			path: '/settings',
			name: 'SettingsRoot',
			component: Settings,
			redirect: { name: 'SettingsGeneral' }, 
			meta: {
				breadcrumb: 'Settings',
				description: "Customize your project's settings...",
			},
			children: [
				{
					path: 'general',
					name: 'SettingsGeneral',
					component: Settings,
					meta: { breadcrumb: 'General' }
				},
				{
					path: 'media',
					name: 'SettingsMedia',
					component: Settings,
					meta: { breadcrumb: 'Media' }
				},
				{
					path: 'uploader',
					name: 'SettingsUploader',
					component: Settings,
					meta: { breadcrumb: 'Uploader' }
				},
				{
					path: 'metadata',
					name: 'SettingsMetadata',
					component: Settings,
					meta: { breadcrumb: 'MetaData' }
				},
				{
					path: 'text',
					name: 'SettingsText',
					component: Settings,
					meta: { breadcrumb: 'Text' }
				},
				{
					path: 'ai',
					name: 'SettingsAi',
					component: Settings,
					meta: { breadcrumb: 'AI Interface' }
				},
			]
		},
		{
			path: '/media',
			name: 'Media',
			component: Media,
			props:true,
			meta: {
				topcomponent: 'MediaSearchField',
				breadcrumb: 'Manage Media',
				mmbreadcrumb: 'Manage Assets',
				description: 'Effortlessly manage, moderate, and transfer your content with intuitive tools, ensuring smooth workflows and content control.'
			},
		},
		{
			path: '/assets',
			name: 'MediaManager',
			component: MediaBrowser,
			props: true,
			meta: {
				breadcrumb: 'Manage Assets',
				description: 'Browse, upload, publish, and manage your media assets.',
			},
		},
		{
			path: '/media/:id',
			name: 'Media Item',
			component: MediaItem,
			meta: {
				breadcrumb: (route: RouteLocationNormalized) => ` ${route.params.id}`,
					parent: 'Media', // Parent route for breadcrumb hierarchy
				description: '',
			},
		},
		{
			path: '/members',
			name: 'Members',
			props:true,
			component: Members,
			meta: {
				breadcrumb: 'Manage Users',
				mmbreadcrumb: 'Manage Users',
				description: 'Effortlessly manage users with this intuitive interface. View the complete user list, update profiles, assign roles, and control access permissions to ensure smooth and secure operations.',
			}
		},
		{
			path: '/member/:id',
			name: 'Member',
			component: Member,
			meta: {
				breadcrumb: (route: RouteLocationNormalized) => ` ${route.params.id}`,
					parent: 'Members', // Parent route for breadcrumb hierarchy
				description: '',
			},
		},
		{
			path: '/contests',
			name: 'Contests',
			component: Contests,
			meta: {
				breadcrumb: 'Contests',
				description: 'Create and manage contests, campaigns, and user engagement activities.',
			},
		},
		{
			path: '/video-portal/:appId?',
			name: 'VideoPortal',
			component: VideoPortal,
			meta: {
				breadcrumb: 'Video Portal',
				description: 'Configure and publish a video portal for your audience.',
			},
		},
		{
			path: '/collections',
			name: 'Collections',
			component: Collections,
			meta: {
				breadcrumb: 'Collections',
				description: 'Organize media into curated collections. Create manual or smart collections with dynamic criteria for efficient content management.',
			},
		},
		{
			path: '/reports',
			name: 'Reports',
			component: Reports,
			meta: {
				breadcrumb: 'Reports & Analytics',
				description: 'View detailed analytics and reports on uploads, views, bandwidth usage, and member activity to monitor platform performance.',
			},
		},
		{
			path: '/transfers',
			name: 'Transfers',
			component: TransferManager,
			meta: {
				breadcrumb: 'Transfer Manager',
				description: 'Manage content transfers between platforms. Configure transfer profiles, monitor queue status, and track transfer progress.',
			},
		},
		{
			path: '/converter-queue',
			name: 'ConverterQueue',
			component: ConverterQueue,
			meta: {
				breadcrumb: 'Converter Queue',
				description: 'Monitor and manage media conversion progress.',
			},
		},
		{
			path: '/feeds',
			name: 'Feeds',
			component: FeedManager,
			meta: {
				breadcrumb: 'Feed Manager',
				description: 'Import and manage content from RSS, Atom, and JSON feeds. Configure auto-import settings and monitor feed status.',
			},
		},
		{
			path: '/notifications',
			name: 'Notifications',
			component: NotificationManager,
			meta: {
				breadcrumb: 'Notifications',
				description: 'Configure email templates, notification rules, and review notification delivery logs.',
			},
		},
		{
			path: '/widgets',
			name: 'Widgets',
			component: WidgetManager,
			meta: {
				breadcrumb: 'Widget Manager',
				description: 'Create and manage embeddable content widgets. Generate embed codes for galleries, players, and other widget types.',
			},
		},
		{
			path: '/validation',
			name: 'Validation',
			component: ValidationManager,
			meta: {
				breadcrumb: 'Content Validation',
				description: 'Review, approve, or deny uploaded content. Manage content moderation workflow with bulk actions.',
			},
		},
		{
			path: '/audit-log',
			name: 'AuditLog',
			component: AuditLog,
			meta: {
				breadcrumb: 'Audit Log',
				description: 'Track all actions and changes on the platform. Filter by action type, entity, and search for specific events.',
			},
		},
		{
			path: '/builder',
			name: 'Builder',
			component: BuilderView,
			meta: {
				breadcrumb: 'Builder',
				description: 'Visual builder for creating custom layouts and templates.',
			},
		},
		{
			path: '/preview',
			name: 'Preview',
			component: PreviewView,
			meta: {
				breadcrumb: 'Preview',
				description: 'Preview your content and configurations before publishing.',
			},
		},
		{
			path: '/permissions',
			name: 'Permissions',
			component: Permissions,
			meta: {
				breadcrumb: 'Permissions',
				description: 'Manage permission groups and access control for your project.',
			},
		},
		{
			path: '/gamification',
			name: 'Gamification',
			component: Gamification,
			meta: {
				breadcrumb: 'Gamification',
				description: 'Configure scoring rules, rewards, milestones, and progression paths. Define how users earn points and unlock achievements through platform actions.',
			},
		},
		{
			path: '/ml-training',
			name: 'MLTraining',
			component: MLTraining,
			meta: {
				breadcrumb: 'ML Training',
				description: 'Train custom content authenticity detection models using your moderation decisions. Monitor training progress and model accuracy.',
			},
		},
		{
			path: '/metadata-config',
			name: 'MetadataConfig',
			component: MetadataConfig,
			meta: {
				breadcrumb: 'Metadata Configuration',
				description: 'Configure custom metadata fields for media items.',
			},
		},
		{
			path: '/api-test',
			name: 'ApiTest',
			component: ApiTest,
			meta: {
				breadcrumb: 'API Test',
				description: 'Compare GQL and JSON API responses side-by-side to verify migration parity.',
			},
		},
		{
			path: '/403',
			name: 'Forbidden',
			component: ErrorPage,
			meta: {
				type: '403',
				hidePageHeader: true,
			},
		},
		{
			path: '/404',
			name: 'NotFound',
			component: ErrorPage,
			meta: {
				type: '404',
				hidePageHeader: true,
			},
		},
		{
			path: '/:pathMatch(.*)*',  // ⬅️ Catch all unknown paths
			redirect: '/404',
		},
	]
})

export default router
